
import React from 'react';
import { Project, AppTheme } from '../types';

interface JobBookProps {
  projects: Project[];
  onSelectProject: (p: Project) => void;
  theme: AppTheme;
}

const JobBook: React.FC<JobBookProps> = ({ projects, onSelectProject, theme }) => {
  const getCardStyle = () => {
    switch(theme) {
      case AppTheme.BAMBOO: return "bg-[#faedcd] border-[#ccd5ae] text-[#283618]";
      case AppTheme.OCEAN: return "bg-white border-[#d1e9ff] text-[#003049]";
      default: return "bg-white border-transparent hover:border-slate-100 text-black";
    }
  };

  const getSubtextStyle = () => {
    switch(theme) {
      case AppTheme.BAMBOO: return "text-[#606c38]";
      case AppTheme.OCEAN: return "text-[#669bbc]";
      default: return "text-slate-500";
    }
  };

  return (
    <div className="p-8 md:p-12 max-w-6xl mx-auto space-y-12 animate-in fade-in duration-1000">
      <header className="space-y-3">
        <p className={`text-[9px] font-black uppercase tracking-[0.4em] ${getSubtextStyle()}`}>Owner's Command Center</p>
        <h1 className="text-4xl font-extralight tracking-tight">Margin Defense</h1>
        <p className={`max-w-lg text-sm font-light leading-relaxed ${getSubtextStyle()}`}>
          Prevent uncompensated scope creep and sub-performance bleed. Defend your expected margin with forensic precision.
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        {projects.map((project) => (
          <div 
            key={project.id}
            onClick={() => onSelectProject(project)}
            className={`group rounded-[2.5rem] p-8 hover:shadow-[0_15px_60px_rgba(0,0,0,0.03)] transition-all duration-500 cursor-pointer flex flex-col border active:scale-[0.99] ${getCardStyle()}`}
          >
            <div className="flex justify-between items-start mb-8 gap-4">
              <div className="flex-1 min-w-0">
                <h2 className="text-xl font-light tracking-tight mb-1 truncate group-hover:tracking-normal transition-all">{project.name}</h2>
                <div className="flex items-center gap-2">
                  <span className="text-[9px] opacity-40 font-black uppercase tracking-widest truncate">{project.location}</span>
                  <div className={`shrink-0 w-1 h-1 rounded-full ${
                    project.marginStatus === 'Green' ? 'bg-emerald-400' :
                    project.marginStatus === 'Yellow' ? 'bg-amber-400' : 'bg-rose-400'
                  }`}></div>
                </div>
              </div>
              <div className="text-right shrink-0">
                <p className="text-[8px] font-black opacity-30 uppercase tracking-widest mb-0.5">Health</p>
                <span className={`text-lg font-medium ${project.cpi < 1 ? 'text-rose-500' : ''}`}>
                  {project.cpi.toFixed(2)}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-8 border-t border-black/5 pt-8">
              <div className="space-y-0.5 min-w-0">
                <p className="text-[8px] opacity-40 font-black uppercase tracking-widest truncate">Exposure</p>
                <p className={`text-2xl font-light tracking-tighter truncate ${project.unbilledExposure > 200000 ? 'text-rose-600' : ''}`}>
                  ${(project.unbilledExposure / 1000).toFixed(0)}K
                </p>
              </div>
              <div className="space-y-0.5 min-w-0">
                <p className="text-[8px] opacity-40 font-black uppercase tracking-widest truncate">Variance</p>
                <p className="text-2xl font-light tracking-tighter truncate">
                  ${((project.forecastAtCompletion - project.contractAmount) / 1000).toFixed(0)}K
                </p>
              </div>
            </div>

            <div className="mt-8 flex items-center justify-between">
              <span className="text-[9px] font-black uppercase tracking-[0.2em] opacity-0 group-hover:opacity-100 transition-all">Radar Launch →</span>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 ${theme === AppTheme.ZEN ? 'bg-[#f2f2f2] group-hover:bg-black group-hover:text-white' : 'bg-black/5 group-hover:bg-black group-hover:text-white'}`}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
              </div>
            </div>
          </div>
        ))}

        <div className={`bg-transparent border-2 border-dashed rounded-[2.5rem] p-8 flex flex-col items-center justify-center text-center group hover:border-current transition-all cursor-pointer opacity-30 hover:opacity-100 ${theme === AppTheme.BAMBOO ? 'border-[#ccd5ae]' : theme === AppTheme.OCEAN ? 'border-[#d1e9ff]' : 'border-slate-200'}`}>
          <div className="w-12 h-12 rounded-2xl bg-white shadow-sm flex items-center justify-center mb-4 group-hover:scale-110 transition-all">
            <svg className="w-6 h-6 opacity-20 group-hover:opacity-100" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
          </div>
          <h3 className="text-lg font-light">New Project</h3>
          <p className="text-[9px] font-bold uppercase tracking-widest mt-1">Deploy Protection</p>
        </div>
      </div>
    </div>
  );
};

export default JobBook;
